import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit, FaTrash, FaEye, FaInbox, FaSort, FaSortAlphaDown, FaSortAlphaUp,
  FaChevronDown, FaChevronUp, FaEyeSlash,
} from 'react-icons/fa';
import { getServices, deleteService } from './serviceService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import FeedbackModal from '../../components/shared/ModalNotification';
import Pagination from '../../components/shared/Pagination';
import { ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  EmptyState,
  FilterSection,
  FilterGroup,
  FilterLabel,
  FilterButton
} from '../../components/styled/GridComponents';

// ── Accordion list ───────────────────────────────────────────────────────────
// Replaces the card grid: descriptions here run to ~1,600 characters, so showing
// them all at once made the page unscannable. Collapsed by default; the row
// shows just what you need to find a service (name + price), and expands on tap.
// Built as a stacked list rather than a table so it works on a phone.

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.$inactive ? p.theme.colors.inputGray + '33' : p.theme.colors.inputGray + '22'};
  border-radius: 12px;
  overflow: hidden;
  opacity: ${p => (p.$inactive ? 0.6 : 1)};
`;

// The whole header is the accordion toggle — a big tap target on mobile.
const RowHead = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  color: inherit;

  &:hover { background: ${p => p.theme.colors.primary}0D; }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    padding: 12px 14px;
    row-gap: 6px;
  }
`;

const Name = styled.div`
  flex: 1;
  min-width: 0;
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  word-break: break-word;
`;

const InactiveTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  padding: 2px 7px; border-radius: 10px;
  background: ${p => p.theme.colors.inputGray}33;
  color: ${p => p.theme.colors.textSecondary};
`;

const Price = styled.div`
  flex-shrink: 0;
  color: ${p => p.theme.colors.primary};
  font-weight: 700;
  font-size: 1rem;
  white-space: nowrap;

  small {
    display: block;
    font-size: 0.66rem;
    font-weight: 500;
    color: ${p => p.theme.colors.textSecondary};
    text-align: right;
  }

  @media (max-width: 600px) { font-size: 0.95rem; }
`;

const Chevron = styled.span`
  flex-shrink: 0;
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  font-size: 0.8rem;
`;

const Body = styled.div`
  padding: 0 16px 14px;
  border-top: 1px solid ${p => p.theme.colors.inputGray}1A;

  p {
    margin: 12px 0 0;
    color: ${p => p.theme.colors.textSecondary};
    font-size: 0.88rem;
    line-height: 1.55;
    white-space: pre-wrap;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 16px;
  border-top: 1px solid ${p => p.theme.colors.inputGray}1A;
  background: ${p => p.theme.colors.background}55;

  @media (max-width: 600px) { > * { flex: 1; justify-content: center; } }
`;

const Act = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px; cursor: pointer; font-size: 0.8rem;
  background: transparent;
  border: 1px solid ${p => p.theme.colors.inputGray}44;
  color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.textSecondary};

  &:hover {
    border-color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary};
    color: ${p => p.$danger ? p.theme.colors.error : p.theme.colors.primary};
  }
`;

const Skeleton = styled.div`
  height: 58px; border-radius: 12px;
  background: ${p => p.theme.colors.surface};
  animation: pulse 1.2s ease-in-out infinite;
  @keyframes pulse { 0%,100% { opacity: 0.5; } 50% { opacity: 0.85; } }
`;

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';
};

export default function ServiceList() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);   // rows are compact now that descriptions collapse
  const [expanded, setExpanded] = useState(new Set());
  // 5 of the 44 services are inactive and were previously indistinguishable.
  const [activeFilter, setActiveFilter] = useState('active');
  const [sortConfig, setSortConfig] = useState({ field: 'service_name', direction: 'asc' });
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const navigate = useNavigate();

  const showNotification = (message, isSuccess = true) => {
    setFeedbackMessage(message);
    setFeedbackSuccess(isSuccess);
    setShowFeedback(true);
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await getServices();
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    setSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleExpanded = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const activeCounts = useMemo(() => ({
    active: services.filter(s => s.isActive !== false).length,
    all: services.length,
  }), [services]);

  const sortedAndFilteredServices = useMemo(() => {
    let filtered = services.filter(service =>
      service.service_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (activeFilter === 'active') filtered = filtered.filter(s => s.isActive !== false);

    return filtered.sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      if (sortConfig.field === 'price_per_sqft') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (sortConfig.direction === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });
  }, [services, searchQuery, sortConfig, activeFilter]);

  // Get current services for pagination
  const indexOfLastService = currentPage * itemsPerPage;
  const indexOfFirstService = indexOfLastService - itemsPerPage;
  const currentServices = sortedAndFilteredServices.slice(indexOfFirstService, indexOfLastService);

  const handleDelete = async () => {
    try {
      const response = await deleteService(serviceToDelete);
      await fetchServices();
      const msg = response?.data?.message || 'Service deleted successfully.';
      showNotification(msg, true);
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Failed to delete service.';
      console.error('Error deleting service:', error);
      showNotification(msg, false);
    } finally {
      setDeleteModalOpen(false);
      setServiceToDelete(null);
    }
  };

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Services Management"
        onSearch={setSearchQuery}
        onAdd={() => navigate('/services/new')}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Sort by:</FilterLabel>
          <FilterButton 
            isActive={sortConfig.field === 'service_name'}
            onClick={() => handleSort('service_name')}
          >
            {sortConfig.field === 'service_name' ? 
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
              : <FaSort />
            }
            Service Name
          </FilterButton>
          <FilterButton 
            isActive={sortConfig.field === 'price_per_sqft'}
            onClick={() => handleSort('price_per_sqft')}
          >
            {sortConfig.field === 'price_per_sqft' ? 
              (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />) 
              : <FaSort />
            }
            Price
          </FilterButton>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Show:</FilterLabel>
          <FilterButton isActive={activeFilter === 'active'} onClick={() => setActiveFilter('active')}>
            Active ({activeCounts.active})
          </FilterButton>
          <FilterButton isActive={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>
            All ({activeCounts.all})
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <List>
        {loading ? (
          [...Array(6)].map((_, index) => <Skeleton key={index} />)
        ) : sortedAndFilteredServices.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No services found. Add a new service to get started.</p>
          </EmptyState>
        ) : (
          currentServices.map(service => {
            const isOpen = expanded.has(service.id);
            const inactive = service.isActive === false;
            const description = (service.description || '').trim();
            return (
              <Row key={service.id} $inactive={inactive}>
                <RowHead
                  type="button"
                  onClick={() => toggleExpanded(service.id)}
                  aria-expanded={isOpen}
                  title={description ? 'Show description' : 'No description'}
                >
                  <Name>
                    {service.service_name}
                    {inactive && <InactiveTag><FaEyeSlash size={9} /> Inactive</InactiveTag>}
                  </Name>
                  <Price>
                    {money(service.price_per_sqft)}
                    <small>base price</small>
                  </Price>
                  <Chevron>{isOpen ? <FaChevronUp /> : <FaChevronDown />}</Chevron>
                </RowHead>

                {isOpen && (
                  <>
                    <Body>
                      <p>{description || 'No description written for this service yet.'}</p>
                    </Body>
                    <Actions>
                      <Act onClick={() => navigate(`/services/view/${service.id}`)}><FaEye /> View</Act>
                      <Act onClick={() => navigate(`/services/edit/${service.id}`)}><FaEdit /> Edit</Act>
                      <Act
                        $danger
                        onClick={() => { setServiceToDelete(service.id); setDeleteModalOpen(true); }}
                      ><FaTrash /> Delete</Act>
                    </Actions>
                  </>
                )}
              </Row>
            );
          })
        )}
      </List>

      {!loading && sortedAndFilteredServices.length > itemsPerPage && (
        <Pagination
          itemsPerPage={itemsPerPage}
          totalItems={sortedAndFilteredServices.length}
          paginate={setCurrentPage}
          currentPage={currentPage}
        />
      )}

      <Modal
        isOpen={deleteModalOpen}
        title="Delete Service"
        message="Are you sure you want to delete this service? If it's used by existing leads or estimates, it will be archived instead."
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setServiceToDelete(null);
        }}
      />

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={() => setShowFeedback(false)}
      />
    </DashboardLayout>
  );
}